const ANIMATIONS = {
  default: {
    keyframes: [
      { transform: "translateY(20px)", opacity: 0 },
      { transform: "translateY(0)", opacity: 1 },
    ],
    options: {
      delay: 250,
      duration: 750,
      easing: 'ease-out',
      fill: 'backwards',
    },
    stagger: 75,
  },
  "slide-in": {
    keyframes: [
      { transform: "translateX(30px)" },
      { transform: "translateX(0)" },
    ],
    options: {
      delay: 250,
      duration: 750,
      easing: 'ease-out',
      fill: 'backwards',
    },
    stagger: 75,
  },
  "slide-fade-in": {
    keyframes: [
      { transform: "translateX(30px)", opacity: 0 },
      { transform: "translateX(0)", opacity: 1 },
    ],
    options: {
      delay: 250,
      duration: 500,
      easing: 'ease-out',
      fill: 'backwards',
    },
    stagger: 75,
  },
  "fade-in": {
    keyframes: [
      { opacity: 0 },
      { opacity: 1 },
    ],
    options: {
      duration: 750,
      easing: 'ease-out',
      fill: 'backwards',
    },
    stagger: 50,
  },
  "none": {
    keyframes: [
      {},
      {},
    ],
    options: {
      duration: 750,
      easing: 'ease-out',
      fill: 'backwards',
    },
    stagger: 50,
  }
};

const normalizeTargets = data => {
  const targets = data.targets.map(target => {
    const animation = target.animation && target.animation in ANIMATIONS
      ? target.animation
      : data.animation && data.animation in ANIMATIONS
        ? data.animation
        : 'default';

    let subTarget = null;

    if (target.subTarget) {
      subTarget = {
        selector: typeof target.subTarget == 'string' ? target.subTarget : target.subTarget.selector,
        stagger: target.subTarget?.stagger ? target.subTarget?.stagger : target.stagger || ANIMATIONS[animation].stagger,
        animation: target.subTarget?.animation && target.subTarget?.animation in ANIMATIONS ? target.subTarget.animation : animation,
      }
    }

    const normalizedTarget = {
      selector: typeof target == 'string' ? target : target.selector,
      subTarget: subTarget,
      stagger: target?.stagger || ANIMATIONS[animation].stagger,
      animation,
    };

    return normalizedTarget
  });

  return targets;
};

class AnimateInView extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion)').matches) {
      this.setAttribute('loaded', '');
      return;
    }

    try {
      const data = JSON.parse(this.querySelector('script').innerHTML);
      this.targets = normalizeTargets(data);
    } catch (error) {
      console.warn('Error parsing animation data', error);
      return;
    }

    this.animations = new WeakMap();
    this.observers = [];

    this.targets.forEach(target => {
      this.targetEls = this.section.querySelectorAll(target.selector);

      this.targetEls.forEach(targetEl => {
        this.initAnimation({ target, targetEl });

        if (!target.subTarget) return;

        const subTargetEls = targetEl.querySelectorAll(target.subTarget.selector);
        subTargetEls.forEach(subTargetEl => this.initAnimation({ target: target.subTarget, targetEl: subTargetEl }));
      });

      const observer = new IntersectionObserver((entries) => {
        entries
          .filter(entry => entry.isIntersecting)
          .forEach((entry, i) => {
            observer.unobserve(entry.target);

            const { stagger } = this.animations.get(entry.target);
            const delay = i * stagger;
            this.playAnimation(entry.target, delay);

            if (!target.subTarget) return;

            const subTargetEls = entry.target.querySelectorAll(target.subTarget.selector);
            subTargetEls.forEach((subTargetEl, i) => {
              const { stagger } = this.animations.get(subTargetEl);

              const subTargetDelay = i * stagger + delay;
              this.playAnimation(subTargetEl, subTargetDelay);
            });
          })
      }, {
        rootMargin: "0% 0% -150px 0%",
      });

      this.targetEls.forEach(targetEl => observer.observe(targetEl));

      this.observers.push(observer);

      this.setAttribute('loaded', '');
    });
  }

  playAnimation(el, delay) {
    const { animation } = this.animations.get(el);

    if (!animation) {
      console.warn('no animation found for', el);
      return;
    }

    if (delay) {
      window.setTimeout(() => animation.play(), delay);
    } else {
      animation.play();
    }
  }

  initAnimation({ target, targetEl }) {
    const { keyframes, options } = ANIMATIONS[target.animation];

    const keyframeEffect = new KeyframeEffect(targetEl, keyframes, options);
    const animation = new Animation(keyframeEffect);
    animation.pause();

    this.animations.set(targetEl, { animation, stagger: target.stagger });
  }

  get section() {
    return this.parentElement;
  }

  disconnectedCallback() {
    this.observers.forEach(observer => observer.disconnect());
  }
}

if (!customElements.get("animate-in-view")) {
  customElements.define("animate-in-view", AnimateInView);
}
